import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfigStore } from '@/client/services/state/configStore';
import { useConsentStore, ConsentCategory } from '@/client/services/state/consentStore';

// vanilla-cookieconsent uses `export = CookieConsent` (CJS namespace); dynamic import
// yields the namespace as the module object itself (no .default unwrapping needed in
// the bundler, but we guard just in case to stay safe across bundler versions).
type CookieConsentApi = typeof import('vanilla-cookieconsent');

async function loadCC(): Promise<CookieConsentApi> {
  const mod = await import('vanilla-cookieconsent');
  return ((mod as unknown as { default?: CookieConsentApi }).default ?? mod) as CookieConsentApi;
}

export default function CookieConsentBanner() {
  const { t, i18n } = useTranslation();
  const { config } = useConfigStore();
  const enabled = !!config?.features?.consent_management_enabled;

  useEffect(() => {
    let active = true;

    const activateAll = async () => {
      // Flag off: run the library headless so deferred `text/plain` data-category
      // scripts in index.html still execute (accept-all unblocks them).
      const cc = await loadCC();
      if (!active) return;
      await cc.run({
        cookie: { name: 'cc_cookie', useLocalStorage: true },
        autoShow: false,
        categories: {
          essential: { enabled: true, readOnly: true },
          functional: {},
          analytics: {},
          marketing: {},
        },
        // v3 reads language.translations unconditionally inside run(); without
        // this it throws and the accept-all below never executes.
        language: {
          default: 'en',
          translations: { en: { consentModal: {}, preferencesModal: {} } },
        },
      } as unknown as Parameters<CookieConsentApi['run']>[0]);
      cc.acceptCategory('all');
      // Mirror the grant into the store so React-driven trackers (Intercom, Zendesk,
      // NPS, analytics identify/track) run for flag-off tenants too.
      useConsentStore.getState().setFromLibrary(['functional', 'analytics', 'marketing']);
    };

    const runConsent = async () => {
      const cc = await loadCC();
      await import('vanilla-cookieconsent/dist/cookieconsent.css');
      await import('./consentTheme.css');
      if (!active) return;

      const sync = () => {
        const accepted = cc.getUserPreferences().acceptedCategories as ConsentCategory[];
        useConsentStore.getState().setFromLibrary(accepted);
      };

      await cc.run({
        cookie: { name: 'cc_cookie', useLocalStorage: true },
        categories: {
          essential: { enabled: true, readOnly: true },
          functional: {
            autoClear: {
              cookies: [{ name: /^intercom-/ }, { name: /^__zlcmid/ }, { name: /^_zendesk/ }],
            },
          },
          analytics: {
            autoClear: {
              cookies: [{ name: /^_ga/ }, { name: /^_gid/ }],
            },
          },
          marketing: {},
        },
        language: {
          default: i18n.language || 'en',
          translations: {
            [i18n.language || 'en']: {
              consentModal: {
                title: t('consent.banner.title'),
                description: t('consent.banner.description'),
                acceptAllBtn: t('consent.banner.acceptAll'),
                acceptNecessaryBtn: t('consent.banner.rejectAll'),
                showPreferencesBtn: t('consent.banner.managePreferences'),
              },
              preferencesModal: {
                title: t('consent.modal.title'),
                acceptAllBtn: t('consent.modal.acceptAll'),
                acceptNecessaryBtn: t('consent.modal.rejectAll'),
                savePreferencesBtn: t('consent.modal.save'),
                closeIconLabel: t('consent.modal.close'),
                sections: (['essential', 'functional', 'analytics', 'marketing'] as ConsentCategory[]).map(
                  (cat) => ({
                    title: t(`consent.categories.${cat}.title`),
                    description: t(`consent.categories.${cat}.description`),
                    linkedCategory: cat,
                  }),
                ),
              },
            },
          },
        },
        onFirstConsent: () => sync(),
        onConsent: () => sync(),
        onChange: () => sync(),
      } as Parameters<CookieConsentApi['run']>[0]);

      useConsentStore.getState().registerPreferencesOpener(() => cc.showPreferences());
      sync();
    };

    const reportConsentLoadFailure = (err: unknown) => {
      (window as unknown as { newrelic?: { noticeError?: (e: unknown) => void } }).newrelic?.noticeError?.(err);
    };
    if (enabled) {
      runConsent().catch(reportConsentLoadFailure);
    } else {
      activateAll().catch(reportConsentLoadFailure);
    }

    return () => {
      active = false;
      useConsentStore.getState().registerPreferencesOpener(null);
    };
    // reason: re-run when the tenant flag flips (config load) or when the app
    // language changes (re-registers the current language's translations).
    // t is intentionally omitted — it is stable across language changes; only
    // i18n.language changing means new translations must be registered.
  }, [enabled, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
